import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShoppingCart, Package, Minus, Plus, Receipt, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { SearchBox } from "@/components/ContentParts";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const CATS = [["all", "All"], ["safety", "Safety"], ["lens_optics", "Optics"], ["accessory", "Accessories"], ["machine", "Machines"], ["consumables", "Consumables"], ["marketing", "Marketing"], ["other", "Other"]];
export const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const productImg = (p) => p.image_url || (p.image_base64 ? (p.image_base64.startsWith("data:") ? p.image_base64 : `data:image/png;base64,${p.image_base64}`) : null);

export const ProductImage = ({ p, className = "" }) => {
  const src = productImg(p);
  return (
    <div className={`flex items-center justify-center overflow-hidden rounded-xl bg-white ${className}`}>
      {src ? <img src={src} alt={p.name} className="h-full w-full object-contain" /> : <ImageOff className="text-neutral-400" />}
    </div>
  );
};

export const CartButton = () => {
  const { totalCount } = useCart();
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/store/cart")} data-testid="cart-btn" className="relative flex h-11 items-center gap-2 rounded-xl bg-[var(--accent)] px-3 text-xs font-extrabold text-black">
      <ShoppingCart size={18} /> Cart
      {totalCount > 0 && <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1 text-[10px] font-black text-black" data-testid="cart-count">{totalCount > 99 ? "99+" : totalCount}</span>}
    </button>
  );
};

export function StoreIndex() {
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);
  useEffect(() => { api("/store/products").then(setProducts).catch((e) => setErr(e.message)); }, []);

  const shown = useMemo(() => (products || []).filter((p) => (cat === "all" || p.category === cat) && (!q.trim() || `${p.name} ${p.description} ${p.sku || ""}`.toLowerCase().includes(q.trim().toLowerCase()))), [products, cat, q]);

  return (
    <PortalPage eyebrow="ALR STORE" title="Parts, safety & supplies" testId="store-page" width="max-w-6xl"
      actions={<><button onClick={() => navigate("/store/orders")} data-testid="my-orders-btn" className="flex h-11 items-center gap-2 rounded-xl border border-[#333] bg-[#1A1A1A] px-3 text-xs font-bold"><Receipt size={16} /> My Orders</button><CartButton /></>}>
      <SearchBox value={q} onChange={setQ} placeholder="Search products, SKUs…" testId="store-search" />
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {CATS.map(([k, label]) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`store-cat-${k}`} className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold ${cat === k ? "border-[var(--accent)] bg-[var(--accent)] text-black" : "border-[#333] bg-[#1A1A1A] text-neutral-400"}`}>{label}</button>
        ))}
      </div>
      <ErrorNote>{err}</ErrorNote>
      {!products && !err && <Spinner />}
      {products && shown.length === 0 && <p className="text-sm text-neutral-400" data-testid="store-empty">No products match.</p>}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="store-grid">
        {shown.map((p) => (
          <button key={p.id} onClick={() => navigate(`/store/${p.id}`)} data-testid={`product-card-${p.id}`} className="group flex flex-col overflow-hidden rounded-2xl border border-[#333] bg-[#1A1A1A] text-left transition-[transform,border-color] hover:-translate-y-0.5 hover:border-[var(--accent)]">
            <ProductImage p={p} className="aspect-square rounded-none" />
            <div className="flex flex-1 flex-col p-3">
              <p className="line-clamp-2 text-sm font-semibold">{p.name}</p>
              {p.sku && <p className="text-[11px] text-neutral-500">SKU {p.sku}</p>}
              <p className="mt-auto pt-2 font-display text-xl font-bold text-[var(--accent)]">{usd(p.price_usd)}</p>
              {(!p.in_stock || !p.purchasable_online) && <p className="text-[11px] font-bold text-[#F5A623]">{!p.in_stock ? "Out of stock" : "Call to order"}</p>}
            </div>
          </button>
        ))}
      </div>
    </PortalPage>
  );
}

export function StoreProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [machine, setMachine] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api(`/store/products/${id}`).then(setP).catch((e) => setErr(e.message)); }, [id]);
  const canBuy = p?.in_stock && p?.purchasable_online;
  const needsMachine = p?.machine_options?.length > 0;
  const add = (go) => {
    if (needsMachine && !machine) return toast.error("Choose which machine this is for first.");
    addItem(p, qty, needsMachine ? machine : null);
    if (go) navigate("/store/cart"); else toast.success(`Added ${qty} × ${p.name}${machine ? ` (${machine})` : ""} to cart`);
  };

  return (
    <PortalPage eyebrow="ALR STORE" title={p?.name || "Product"} back="/store" backLabel="Store" testId="product-page" width="max-w-5xl" actions={<CartButton />}>
      <ErrorNote>{err}</ErrorNote>
      {!p && !err && <Spinner />}
      {p && (
        <div className="grid gap-6 md:grid-cols-2">
          <ProductImage p={p} className="aspect-square" />
          <div className="space-y-4">
            <p className="font-display text-4xl font-extrabold text-[var(--accent)]" data-testid="product-price">{usd(p.price_usd)}</p>
            {p.sku && <p className="text-sm text-neutral-500">SKU {p.sku}</p>}
            <p className="whitespace-pre-line text-[15px] leading-7 text-neutral-300">{p.description}</p>
            {p.compatible_with?.length > 0 && <p className="text-sm text-neutral-400">Compatible with: <span className="text-white">{p.compatible_with.join(", ")}</span></p>}
            {p.notes && <Card className="p-4 text-sm text-neutral-300">{p.notes}</Card>}
            {canBuy ? (
              <>
                {needsMachine && (
                  <div data-testid="machine-picker">
                    <p className="mb-2 text-sm font-semibold">Which machine is this for? <span className="text-[#FF3333]">*</span></p>
                    <div className="flex flex-wrap gap-2">
                      {p.machine_options.map((m) => (
                        <button key={m} onClick={() => setMachine(m)} data-testid={`machine-option-${m}`}
                          className={`h-11 rounded-xl border px-4 text-sm font-bold transition-colors ${machine === m ? "border-[var(--accent)] bg-[var(--accent)] text-black" : "border-[#333] hover:border-[var(--accent)]"}`}>{m}</button>
                      ))}
                    </div>
                  </div>
                )}
                {p.overnight_usd > 0 && <p className="text-xs text-neutral-400" data-testid="overnight-note">Overnight shipping available at checkout for {usd(p.overnight_usd)}.</p>}
                <div className="flex items-center gap-3">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease" data-testid="qty-minus" className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#333]"><Minus size={16} /></button>
                  <span className="w-10 text-center text-lg font-bold" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(99, q + 1))} aria-label="Increase" data-testid="qty-plus" className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#333]"><Plus size={16} /></button>
                </div>
                <Btn onClick={() => add(false)} data-testid="add-to-cart"><ShoppingCart size={16} /> Add to cart</Btn>
                <Btn variant="outline" onClick={() => add(true)} data-testid="buy-now">Add & go to cart</Btn>
              </>
            ) : (
              <Card className="flex items-center gap-3 p-4 text-sm" data-testid="product-unavailable"><Package className="text-[#F5A623]" />{!p.in_stock ? "Currently out of stock." : "This item isn't sold online — call ALR at 844-527-3799 or email info@ALRClean.com to order."}</Card>
            )}
          </div>
        </div>
      )}
    </PortalPage>
  );
}
